import { useState, useEffect } from 'react'
import { FileUp, FileDown } from 'lucide-react'
import { supabase } from '../../supabase'
import { T, fonts } from '../../styles/theme'
import PrintReport from '../../components/PrintReport'
import ExportModal from '../../components/ExportModal'
import ImportModal from '../../components/ImportModal'
import { WeightChart, BalanceChart, MacroChart, CompositionChart } from '../../components/charts'
import { SectionLabel, Card } from '../../components/ui'
import { bucketModeFor, aggregateHistory } from '../../services/historyAggregation'
import { getBalanceTargets, withHistoricalBalance, resolveTargetsForDate, fillMissingDays } from '../../services/balanceCalc'

function todayStr() { return new Date().toISOString().split('T')[0] }

function jackssonPollock3(sexo, pliegues, edad) {
    const { tricipital, suprailiaco, abdominal, muslo } = pliegues
    let dc
    if (sexo === 'M') {
      const suma = (abdominal || 0) + (muslo || 0) + (tricipital || 0)
      dc = 1.10938 - (0.0008267 * suma) + (0.0000016 * suma * suma) - (0.0002574 * edad)
    } else {
      const suma = (tricipital || 0) + (suprailiaco || 0) + (muslo || 0)
      dc = 1.0994921 - (0.0009929 * suma) + (0.0000023 * suma * suma) - (0.0001392 * edad)
    }
    return Math.round(((4.95 / dc) - 4.5) * 100 * 10) / 10
  }

export default function PacienteDetalle({ patient, onBack, refetch, profesionalId, fetchPatientHistory, eliminarPaciente }) {
  const [nota, setNota] = useState('')
  const [savingNota, setSavingNota] = useState(false)
  const [plan, setPlan] = useState('')
  const [savingPlan, setSavingPlan] = useState(false)
  const [showMetric, setShowMetric] = useState(false)
  const [showGoals, setShowGoals] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [showExport, setShowExport] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [metricTab, setMetricTab] = useState('bascula')
  const [rangeWeeks, setRangeWeeks] = useState(6)
  const [balanceHistory, setBalanceHistory] = useState([])
  const [showHistorial, setShowHistorial] = useState(false)
  const [diarioFecha, setDiarioFecha] = useState(todayStr())
  const [historialFecha, setHistorialFecha] = useState(null)
  const [historialComidas, setHistorialComidas] = useState([])
  const [loadingHistorial, setLoadingHistorial] = useState(false)
  const [graficaTab, setGraficaTab] = useState('balance')
  const [graficaDays, setGraficaDays] = useState(14)
  const [metricForm, setMetricForm] = useState({
    peso_kg: '', grasa_pct: '', masa_magra_pct: '', masa_muscular_kg: '', perimetro_abdominal_cm: '',
    tricipital_mm: '', subescapular_mm: '', suprailiaco_mm: '', abdominal_mm: '', muslo_mm: '', fecha: todayStr(),
  })
  const [showCalculadora, setShowCalculadora] = useState(false)
  const [calcForm, setCalcForm] = useState({
  nivel: patient.activityProfile?.nivel ?? 'moderado',
  entrenamientos_semana: patient.activityProfile?.entrenamientos_semana ?? 3,
  objetivo_tipo: patient.goals?.tipo ?? 'deficit',
  deficit_superavit: 400,
})
  const [calcResult, setCalcResult] = useState(
  patient.activityProfile?.tdee ? {
    bmr: patient.activityProfile.bmr,
    tdee: patient.activityProfile.tdee,
    kcal_objetivo: patient.activityProfile.kcal_objetivo_calculado,
  } : null
)
  const [goalForm, setGoalForm] = useState({
    tipo: patient.goals?.tipo ?? 'deficit',
    kcal_objetivo: patient.goals?.kcal_objetivo ?? '',
    proteina_objetivo: patient.goals?.proteina_objetivo ?? '',
    grasa_objetivo: patient.goals?.grasa_objetivo ?? '',
    carbohidratos_objetivo: patient.goals?.carbohidratos_objetivo ?? '',
    peso_objetivo: patient.goals?.peso_objetivo ?? '',
    grasa_objetivo_pct: patient.goals?.grasa_objetivo_pct ?? '',
  })
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [targetsHistory, setTargetsHistory] = useState([])

  useEffect(() => {
    if (!fetchPatientHistory) return
    let cancelled = false
    fetchPatientHistory(patient.id, graficaDays).then(result => {
      if (!cancelled) setBalanceHistory(result)
    })
    return () => { cancelled = true }
  }, [patient.id, graficaDays]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTargetsHistory() {
    const { data } = await supabase
      .from('patient_targets_history')
      .select('kcal_objetivo, tdee, vigente_desde')
      .eq('paciente_id', patient.id)
      .order('vigente_desde', { ascending: true })
    setTargetsHistory(data ?? [])
  }

  useEffect(() => {
    loadTargetsHistory()
  }, [patient.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function recordTargetsHistory(kcalObjetivo, tdee) {
    await supabase.from('patient_targets_history').insert({
      paciente_id: patient.id,
      kcal_objetivo: kcalObjetivo,
      tdee,
      vigente_desde: todayStr(),
    })
    loadTargetsHistory()
  }

  useEffect(() => {
    let cancelled = false
    supabase.from('patient_plans').select('contenido').eq('paciente_id', patient.id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setPlan(data?.contenido ?? '') })
    return () => { cancelled = true }
  }, [patient.id])
  
  async function loadDayMeals(fecha) {
    setLoadingHistorial(true)
    setHistorialFecha(fecha)
    const { data: log } = await supabase
      .from('daily_logs')
      .select('id')
      .eq('paciente_id', patient.id)
      .eq('fecha', fecha)
      .single()
  
    if (log) {
      const { data } = await supabase
        .from('meals')
        .select(`
          tipo,
          meal_items (
            cantidad_gramos,
            foods ( nombre, kcal_100g, proteina_100g, grasa_100g, carbohidratos_100g )
          )
        `)
        .eq('daily_log_id', log.id)
      setHistorialComidas(data ?? [])
    } else {
      setHistorialComidas([])
    }
    setLoadingHistorial(false)
    setShowHistorial(true)
  }

  const edad = patient.fecha_nacimiento
    ? Math.floor((new Date() - new Date(patient.fecha_nacimiento)) / (365.25 * 24 * 3600 * 1000))
    : null

  // Gráfica de peso filtrada por rango
  const now = new Date()
  const cutoff = new Date(now)
  cutoff.setDate(cutoff.getDate() - rangeWeeks * 7)
  const weightData = patient.allMetrics
    .filter(m => m.peso_kg && new Date(m.fecha) >= cutoff)
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
    .map(m => ({
      label: new Date(m.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
      fecha: m.fecha,
      v: m.peso_kg
    }))


  async function saveNota() {
    if (!nota.trim()) return
    setSavingNota(true)
    await supabase.from('clinical_notes').insert({
      paciente_id: patient.id,
      profesional_id: profesionalId,
      nota: nota.trim(),
      fecha_revision: new Date().toISOString().split('T')[0]
    })
    setNota('')
    setSavingNota(false)
  }

  async function savePlan() {
    setSavingPlan(true)
    await supabase.from('patient_plans').upsert({
      paciente_id: patient.id,
      contenido: plan,
      actualizado_por: profesionalId,
      updated_at: new Date().toISOString(),
    })
    setSavingPlan(false)
  }

  async function saveMetric() {
    let grasa_pct = metricForm.grasa_pct ? parseFloat(metricForm.grasa_pct) : null
    const fecha = metricForm.fecha > todayStr() ? todayStr() : (metricForm.fecha || todayStr())

    if (metricTab === 'pliegues' && edad && patient.sexo) {
      grasa_pct = jackssonPollock3(patient.sexo, {
        tricipital: parseFloat(metricForm.tricipital_mm),
        suprailiaco: parseFloat(metricForm.suprailiaco_mm),
        abdominal: parseFloat(metricForm.abdominal_mm),
        muslo: parseFloat(metricForm.muslo_mm),
        subescapular: parseFloat(metricForm.subescapular_mm),
      }, edad)

      await supabase.from('skinfolds').insert({
        paciente_id: patient.id,
        fecha,
        tricipital_mm: parseFloat(metricForm.tricipital_mm) || null,
        subescapular_mm: parseFloat(metricForm.subescapular_mm) || null,
        suprailiaco_mm: parseFloat(metricForm.suprailiaco_mm) || null,
        abdominal_mm: parseFloat(metricForm.abdominal_mm) || null,
        muslo_mm: parseFloat(metricForm.muslo_mm) || null,
        sexo: patient.sexo,
        edad,
        grasa_pct_calculado: grasa_pct,
      })
    }

    await supabase.from('body_metrics').insert({
      paciente_id: patient.id,
      registrado_por: profesionalId,
      fecha,
      peso_kg: parseFloat(metricForm.peso_kg) || null,
      grasa_pct,
      masa_magra_pct: parseFloat(metricForm.masa_magra_pct) || null,
      masa_muscular_kg: parseFloat(metricForm.masa_muscular_kg) || null,
      perimetro_abdominal_cm: parseFloat(metricForm.perimetro_abdominal_cm) || null,
      metodo: metricTab,
    })

    setShowMetric(false)
    setMetricForm({ peso_kg: '', grasa_pct: '', masa_magra_pct: '', perimetro_abdominal_cm: '', tricipital_mm: '', subescapular_mm: '', suprailiaco_mm: '', abdominal_mm: '', muslo_mm: '', fecha: todayStr() })
    refetch()
  }

  async function saveGoals() {
    const payload = {
      tipo: goalForm.tipo,
      kcal_objetivo: parseFloat(goalForm.kcal_objetivo) || null,
      proteina_objetivo: parseFloat(goalForm.proteina_objetivo) || null,
      grasa_objetivo: parseFloat(goalForm.grasa_objetivo) || null,
      carbohidratos_objetivo: parseFloat(goalForm.carbohidratos_objetivo) || null,
      peso_objetivo: parseFloat(goalForm.peso_objetivo) || null,
      grasa_objetivo_pct: parseFloat(goalForm.grasa_objetivo_pct) || null,
      updated_at: new Date().toISOString(),
    }
    if (patient.goals) {
      await supabase.from('patient_goals').update(payload).eq('paciente_id', patient.id)
    } else {
      await supabase.from('patient_goals').insert({ ...payload, paciente_id: patient.id })
    }
    if (payload.kcal_objetivo) {
      await recordTargetsHistory(payload.kcal_objetivo, patient.activityProfile?.tdee ?? null)
    }
    setShowGoals(false)
    refetch()
  }

  const inp = (label, key, placeholder, type = 'number') => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>{label}</div>
      <input type={type} placeholder={placeholder}
        value={metricForm[key]}
        onChange={e => setMetricForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }}
      />
    </div>
  )

  const goalInp = (label, key, placeholder) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>{label}</div>
      <input type="number" placeholder={placeholder}
        value={goalForm[key]}
        onChange={e => setGoalForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }}
      />
    </div>
  )

  const latest = patient.allMetrics[0]

  function calcularEnergia() {
    if (!patient.fecha_nacimiento || !patient.altura_cm) return null
    const edad = Math.floor((new Date() - new Date(patient.fecha_nacimiento)) / (365.25 * 24 * 3600 * 1000))
    const peso = patient.allMetrics?.[0]?.peso_kg
    if (!peso) return null
  
    // Mifflin-St Jeor
    let bmr
    if (patient.sexo === 'M') {
      bmr = (10 * peso) + (6.25 * patient.altura_cm) - (5 * edad) + 5
    } else {
      bmr = (10 * peso) + (6.25 * patient.altura_cm) - (5 * edad) - 161
    }
  
    const factores = {
      sedentario: 1.2,
      ligero: 1.375,
      moderado: 1.55,
      alto: 1.725,
    }
    const tdee = Math.round(bmr * (factores[calcForm.nivel] ?? 1.55))
    const bmrR = Math.round(bmr)
  
    const kcal_objetivo = calcForm.objetivo_tipo === 'deficit'
      ? tdee - calcForm.deficit_superavit
      : calcForm.objetivo_tipo === 'superavit'
      ? tdee + calcForm.deficit_superavit
      : tdee
  
    return { bmr: bmrR, tdee, kcal_objetivo }
  }
  
  async function saveCalculadora() {
    const result = calcularEnergia()
    if (!result) return
  
    // Guardar en activity_profile
    const existing = patient.activityProfile
    if (existing) {
      await supabase.from('activity_profile').update({
        nivel: calcForm.nivel,
        entrenamientos_semana: calcForm.entrenamientos_semana,
        bmr: result.bmr,
        tdee: result.tdee,
        kcal_objetivo_calculado: result.kcal_objetivo,
      }).eq('paciente_id', patient.id)
    } else {
      await supabase.from('activity_profile').insert({
        paciente_id: patient.id,
        nivel: calcForm.nivel,
        entrenamientos_semana: calcForm.entrenamientos_semana,
        bmr: result.bmr,
        tdee: result.tdee,
        kcal_objetivo_calculado: result.kcal_objetivo,
      })
    }
  
    // Actualizar también los objetivos calóricos
    if (patient.goals) {
      await supabase.from('patient_goals').update({
        kcal_objetivo: result.kcal_objetivo,
      }).eq('paciente_id', patient.id)
    } else {
      await supabase.from('patient_goals').insert({
        paciente_id: patient.id,
        tipo: calcForm.objetivo_tipo,
        kcal_objetivo: result.kcal_objetivo,
      })
    }
  
    await recordTargetsHistory(result.kcal_objetivo, result.tdee)

    setCalcResult(result)
    setShowCalculadora(false)
    refetch()
  }

  return (
    <>
      <style>{`${fonts} * { box-sizing: border-box; margin: 0; padding: 0; } body { background: ${T.bg}; font-family: 'DM Sans', sans-serif; }`}</style>
      <div style={{ maxWidth: 390, margin: '0 auto', minHeight: '100vh', background: T.bg, paddingBottom: 40 }}>

        {/* Header */}
        <div style={{ padding: '52px 20px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 10, background: T.card, border: `1px solid ${T.cardB}`, color: T.text, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{patient.nombre}</div>
            <div style={{ fontSize: 12, color: T.muted }}>{edad ? `${edad} años` : ''} · {patient.goals?.tipo ?? 'Sin objetivo'}</div>
          </div>
          <button onClick={() => setShowExport(true)} title="Exportar paciente" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'none', border: `1px solid ${T.cardB}`, color: T.muted, borderRadius: 10, cursor: 'pointer', marginRight: 6 }}>
            <FileUp size={16} />
          </button>
          <button onClick={() => setShowImport(true)} title="Importar copia de seguridad" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'none', border: `1px solid ${T.cardB}`, color: T.muted, borderRadius: 10, cursor: 'pointer', marginRight: 6 }}>
            <FileDown size={16} />
          </button>
          <button onClick={() => setShowReport(true)} style={{ background: T.blue + '22', border: `1px solid ${T.blue}`, color: T.blue, borderRadius: 10, padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>📄 Informe</button>
        </div>

        {/* Composición */}
        <SectionLabel>Composición corporal</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '0 16px 12px' }}>
  {[
    [latest?.peso_kg ? latest.peso_kg + ' kg' : '—', 'Peso actual'],
    [latest?.grasa_pct ? latest.grasa_pct + '%' : '—', 'Grasa corporal'],
    [latest?.masa_magra_pct ? latest.masa_magra_pct + '%' : '—', 'Masa magra'],
    [latest?.masa_muscular_kg ? latest.masa_muscular_kg + ' kg' : '—', 'Masa muscular'],
    [latest?.perimetro_abdominal_cm ? latest.perimetro_abdominal_cm + ' cm' : '—', 'Perím. abdominal'],
    [latest?.peso_kg && patient.altura_cm
      ? (latest.peso_kg / Math.pow(patient.altura_cm / 100, 2)).toFixed(1)
      : '—', 'IMC'],
  ].map(([v, l]) => (
    <div key={l} style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 14, padding: 14 }}>
      <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'DM Mono, monospace' }}>{v}</div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{l}</div>
    </div>
  ))}
</div>

        <button onClick={() => setShowMetric(true)} style={{ display: 'block', margin: '0 16px 16px', padding: '11px 0', width: 'calc(100% - 32px)', borderRadius: 12, background: T.accentSoft, border: `1px solid ${T.accent}`, color: T.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          ➕ Añadir medición
        </button>
{/* Gasto energético */}
<SectionLabel>
  Gasto energético
</SectionLabel>
<Card>
  {calcResult ? (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
        {[
          ['BMR', calcResult.bmr + ' kcal', 'Basal'],
          ['TDEE', calcResult.tdee + ' kcal', 'Total'],
          ['Objetivo', calcResult.kcal_objetivo + ' kcal', calcForm.objetivo_tipo],
        ].map(([t, v, s]) => (
          <div key={t} style={{ background: T.bg, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t}</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'DM Mono', color: T.accent }}>{v}</div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{s}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>
        Actividad: <strong style={{ color: T.sub }}>{calcForm.nivel}</strong> · {calcForm.entrenamientos_semana} entrenamientos/semana
      </div>
    </>
  ) : (
    <div style={{ fontSize: 13, color: T.muted, marginBottom: 12, textAlign: 'center', padding: '8px 0' }}>
      Sin cálculo energético. Añade datos para calcular BMR y TDEE.
    </div>
  )}
  <button onClick={() => setShowCalculadora(true)} style={{
    width: '100%', padding: '10px 0', borderRadius: 10,
    background: T.accentSoft, border: `1px solid ${T.accent}`,
    color: T.accent, fontSize: 13, cursor: 'pointer', fontWeight: 600
  }}>
    {calcResult ? '✏️ Recalcular' : '🧮 Calcular BMR / TDEE'}
  </button>
</Card>

{/* MODAL CALCULADORA */}
{showCalculadora && (
  <div onClick={e => { if (e.target === e.currentTarget) setShowCalculadora(false) }}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
    <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
      <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Calculadora energética</div>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>
        Fórmula Mifflin-St Jeor · Basado en datos actuales del paciente
      </div>

      {(!patient.fecha_nacimiento || !patient.altura_cm || !patient.allMetrics?.[0]?.peso_kg) && (
        <div style={{ background: 'rgba(251,191,36,0.12)', border: `1px solid ${T.warn}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: T.warn, marginBottom: 16 }}>
          ⚠️ Necesitas tener registrado peso, altura y fecha de nacimiento del paciente
        </div>
      )}

      {/* Datos usados */}
      <div style={{ background: T.bg, borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
        <div style={{ color: T.muted, marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Datos del paciente</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span>⚖️ {patient.allMetrics?.[0]?.peso_kg ?? '—'} kg</span>
          <span>📏 {patient.altura_cm ?? '—'} cm</span>
          <span>🎂 {edad ? edad + ' años' : '—'}</span>
          <span>⚧ {patient.sexo === 'M' ? 'Hombre' : patient.sexo === 'F' ? 'Mujer' : '—'}</span>
        </div>
      </div>

      {/* Nivel actividad */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>Nivel de actividad</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            ['sedentario', 'Sedentario', '×1.2 — Sin ejercicio o muy poco'],
            ['ligero', 'Ligero', '×1.375 — 1-3 días/semana'],
            ['moderado', 'Moderado', '×1.55 — 3-5 días/semana'],
            ['alto', 'Alto', '×1.725 — 6-7 días/semana'],
          ].map(([k, l, desc]) => (
            <button key={k} onClick={() => setCalcForm(f => ({ ...f, nivel: k }))} style={{
              padding: '10px 14px', borderRadius: 10, textAlign: 'left',
              border: `1px solid ${calcForm.nivel === k ? T.accent : T.cardB}`,
              background: calcForm.nivel === k ? T.accentSoft : T.bg,
              cursor: 'pointer',
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: calcForm.nivel === k ? T.accent : T.text }}>{l}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Entrenamientos */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>Entrenamientos por semana</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map(n => (
            <button key={n} onClick={() => setCalcForm(f => ({ ...f, entrenamientos_semana: n }))} style={{
              flex: 1, padding: '8px 0', borderRadius: 8,
              border: `1px solid ${calcForm.entrenamientos_semana === n ? T.accent : T.cardB}`,
              background: calcForm.entrenamientos_semana === n ? T.accentSoft : T.bg,
              color: calcForm.entrenamientos_semana === n ? T.accent : T.muted,
              fontSize: 13, cursor: 'pointer', fontWeight: 600
            }}>{n}</button>
          ))}
        </div>
      </div>

      {/* Objetivo */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>Objetivo</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['deficit', 'Déficit'], ['mantenimiento', 'Mantenimiento'], ['superavit', 'Superávit']].map(([k, l]) => (
            <button key={k} onClick={() => setCalcForm(f => ({ ...f, objetivo_tipo: k }))} style={{
              flex: 1, padding: '10px 0', borderRadius: 10,
              border: `1px solid ${calcForm.objetivo_tipo === k ? T.accent : T.cardB}`,
              background: calcForm.objetivo_tipo === k ? T.accentSoft : T.bg,
              color: calcForm.objetivo_tipo === k ? T.accent : T.muted,
              fontSize: 12, cursor: 'pointer', fontWeight: 600
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Déficit/superávit */}
      {calcForm.objetivo_tipo !== 'mantenimiento' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>
            {calcForm.objetivo_tipo === 'deficit' ? 'Déficit calórico (kcal/día)' : 'Superávit calórico (kcal/día)'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[200, 300, 400, 500, 600].map(n => (
              <button key={n} onClick={() => setCalcForm(f => ({ ...f, deficit_superavit: n }))} style={{
                flex: 1, padding: '8px 0', borderRadius: 8,
                border: `1px solid ${calcForm.deficit_superavit === n ? T.accent : T.cardB}`,
                background: calcForm.deficit_superavit === n ? T.accentSoft : T.bg,
                color: calcForm.deficit_superavit === n ? T.accent : T.muted,
                fontSize: 12, cursor: 'pointer', fontWeight: 600
              }}>{n}</button>
            ))}
          </div>
        </div>
      )}

      {/* Preview resultado */}
      {(() => {
        const r = calcularEnergia()
        if (!r) return null
        return (
          <div style={{ background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: T.accent, fontWeight: 600, marginBottom: 8 }}>Resultado estimado</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: T.muted }}>BMR (basal)</span>
              <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'DM Mono' }}>{r.bmr} kcal</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: T.muted }}>TDEE (total)</span>
              <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'DM Mono' }}>{r.tdee} kcal</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${T.cardB}` }}>
              <span style={{ fontSize: 13, color: T.muted }}>Objetivo diario</span>
              <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'DM Mono', color: T.accent }}>{r.kcal_objetivo} kcal</span>
            </div>
          </div>
        )
      })()}

      <button onClick={saveCalculadora} style={{
        width: '100%', padding: 14, borderRadius: 14,
        background: T.accent, color: '#000',
        fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer'
      }}>
        Guardar y aplicar al paciente
      </button>
    </div>
  </div>
)}

 {/* Diario alimentario */}
<SectionLabel>
  Diario alimentario
</SectionLabel>
<div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 16, margin: '0 16px 12px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
  <div style={{ flex: 1 }}>
    <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Ver comidas registradas de un día</div>
    <input type="date" value={diarioFecha} max={todayStr()}
      onChange={e => setDiarioFecha(e.target.value)}
      style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }}
    />
  </div>
  <button onClick={() => loadDayMeals(diarioFecha)} style={{
    padding: '13px 18px', borderRadius: 12, background: T.accent, color: '#000',
    fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap'
  }}>
    Ver
  </button>
</div>

 {/* Gráficas unificadas */}
<SectionLabel>
  Evolución
</SectionLabel>

{/* Selector gráfica */}
<div style={{ display: 'flex', gap: 6, margin: '0 16px 8px' }}>
  {[['peso', 'Peso'], ['balance', 'Balance'], ['macros', 'Macros'], ['comp', 'Composición']].map(([k, l]) => (
    <button key={k} onClick={() => setGraficaTab(k)} style={{
      flex: 1, padding: '8px 0', borderRadius: 10,
      border: `1px solid ${graficaTab === k ? T.accent : T.cardB}`,
      background: graficaTab === k ? T.accentSoft : T.card,
      color: graficaTab === k ? T.accent : T.muted,
      fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans'
    }}>{l}</button>
  ))}
</div>

{/* Selector tiempo */}
<div style={{ display: 'flex', gap: 6, margin: '0 16px 8px' }}>
  {[[2, '2 sem'], [4, '1 mes'], [12, '3 mes'], [24, '6 mes'], [52, 'Todo']].map(([w, l]) => (
    <button key={w} onClick={() => {
      setRangeWeeks(w)
      setGraficaDays(w * 7)
    }} style={{
      flex: 1, padding: '6px 0', borderRadius: 8,
      border: `1px solid ${rangeWeeks === w ? T.accent : T.cardB}`,
      background: rangeWeeks === w ? T.accentSoft : T.card,
      color: rangeWeeks === w ? T.accent : T.muted,
      fontSize: 11, cursor: 'pointer', fontFamily: 'DM Sans'
    }}>{l}</button>
  ))}
</div>

<div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 16, margin: '0 16px 12px' }}>
  {graficaTab === 'peso' && (
    <WeightChart data={weightData} goal={patient.goals?.peso_objetivo} />
  )}

  {graficaTab === 'balance' && (() => {
    // El día de hoy se excluye: aún está en curso y desvirtúa la
    // gráfica y las medias. Para el dato en tiempo real está el
    // resumen de "Hoy" en la app del paciente.
    const historyDoneRaw = balanceHistory.filter(d => d.fecha !== todayStr())
    const bucketMode = bucketModeFor(rangeWeeks)
    const kcalObj = patient.goals?.kcal_objetivo ?? 2000
    const tdee = patient.activityProfile?.tdee ?? null
    const { minTarget, maxTarget, isApprox } = getBalanceTargets(kcalObj, tdee)
    // Cada día usa el objetivo/TDEE vigente EN ESE MOMENTO, no el
    // actual — un cambio de objetivo no recolorea el pasado.
    // Los días sin ninguna comida registrada se marcan como "sin datos":
    // franja gris en la gráfica, y no cuentan en la media.
    const historyDone = withHistoricalBalance(fillMissingDays(historyDoneRaw, todayStr()), targetsHistory, kcalObj, tdee)
    const historyWithData = historyDone.filter(d => !d.sinDatos)
    const aggregated = aggregateHistory(historyDone, bucketMode)
    const balData = aggregated
    const hayDiasSinDatos = aggregated.some(d => d.sinDatos)
    const avgDailyBalance = historyWithData.length > 0
      ? Math.round(historyWithData.reduce((s, d) => s + d.v, 0) / historyWithData.length)
      : null
    return (
      <>
        <BalanceChart
          data={balData}
          minTarget={minTarget}
          maxTarget={maxTarget}
          height={130}
          onBarClick={bucketMode === 'day' ? d => loadDayMeals(d.fecha) : undefined}
        />
        {hayDiasSinDatos && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.muted }}>
            <div style={{ width: 10, height: 14, borderRadius: 2, background: T.muted, opacity: 0.3 }} />
            Sin registro (no cuenta en las medias)
          </div>
        )}
        {avgDailyBalance != null && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.cardB}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: T.muted }}>Media diaria del periodo{isApprox ? ' (aprox.)' : ''}</span>
            <span style={{ fontSize: 14, fontFamily: 'DM Mono', fontWeight: 700, color: avgDailyBalance <= 0 ? T.accent : T.danger }}>
              {avgDailyBalance > 0 ? '+' : ''}{avgDailyBalance} kcal
            </span>
          </div>
        )}
        {isApprox && (
          <div style={{ fontSize: 11, color: T.warn, marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span>⚠️ Falta calcular el gasto energético real — la franja es aproximada.</span>
            <button onClick={() => setShowCalculadora(true)} style={{ background: 'none', border: 'none', color: T.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, textDecoration: 'underline' }}>
              Calcular
            </button>
          </div>
        )}
        {balData.length >= 2 && (
          <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
            {bucketMode === 'day'
              ? '💡 Toca una barra para ver el registro de comidas de ese día'
              : `Media ${bucketMode === 'week' ? 'semanal' : 'mensual'} · usa "2 sem" para ver día a día y poder tocar una barra`}
          </div>
        )}
      </>
    )
  })()}

  {graficaTab === 'macros' && (() => {
    const historyDone = balanceHistory.filter(d => d.fecha !== todayStr())
    const aggregated = aggregateHistory(historyDone, bucketModeFor(rangeWeeks)).filter(d => !d.sinDatos && d.kcal > 0)
    return (
      <>
        <MacroChart data={aggregated} goals={patient.goals} height={120} />
        <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
          {[['Proteína', T.blue], ['Grasas', T.warn], ['Carbos', T.purple]].map(([l, c]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 3, borderRadius: 2, background: c }} />
              <span style={{ fontSize: 11, color: T.muted }}>{l} <span style={{ opacity: 0.5 }}>— obj.</span></span>
            </div>
          ))}
        </div>
      </>
    )
  })()}

  {graficaTab === 'comp' && (() => {
    const compData = patient.allMetrics
      .filter(m => (m.grasa_pct || m.masa_magra_pct) && new Date(m.fecha) >= cutoff)
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
      .map(m => ({
        label: new Date(m.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
        grasa: m.grasa_pct,
        magra: m.masa_magra_pct,
      }))
    return (
      <>
        <CompositionChart data={compData} height={120} />
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          {[['% Grasa', T.danger], ['% Masa magra', T.accent]].map(([l, c]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 3, borderRadius: 2, background: c }} />
              <span style={{ fontSize: 11, color: T.muted }}>{l}</span>
            </div>
          ))}
        </div>
      </>
    )
  })()}
</div>

        {/* Objetivos */}
        <SectionLabel>Objetivos nutricionales</SectionLabel>
        <Card>
          {[
            ['Tipo', patient.goals?.tipo ?? '—'],
            ['Calorías', patient.goals?.kcal_objetivo ? patient.goals.kcal_objetivo + ' kcal' : '—'],
            ['Proteína', patient.goals?.proteina_objetivo ? patient.goals.proteina_objetivo + ' g' : '—'],
            ['Grasas', patient.goals?.grasa_objetivo ? patient.goals.grasa_objetivo + ' g' : '—'],
            ['Carbohidratos', patient.goals?.carbohidratos_objetivo ? patient.goals.carbohidratos_objetivo + ' g' : '—'],
            ['Peso objetivo', patient.goals?.peso_objetivo ? patient.goals.peso_objetivo + ' kg' : '—'],
            ['Grasa objetivo', patient.goals?.grasa_objetivo_pct ? patient.goals.grasa_objetivo_pct + '%' : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${T.cardB}` }}>
              <span style={{ fontSize: 14, color: T.muted }}>{k}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{v}</span>
            </div>
          ))}
          <button onClick={() => setShowGoals(true)} style={{ width: '100%', marginTop: 12, padding: 12, borderRadius: 12, background: T.accent, color: '#000', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: 14 }}>
            ✏️ Editar objetivos
          </button>
        </Card>

        {/* Nota clínica */}
        <SectionLabel>Nota clínica</SectionLabel>
        <Card>
          <textarea
            value={nota}
            onChange={e => setNota(e.target.value)}
            placeholder="Escribe una nota para esta revisión..."
            style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: 12, color: T.text, fontSize: 13, fontFamily: 'DM Sans', minHeight: 80, resize: 'none', outline: 'none', lineHeight: 1.5 }}
          />
          <button onClick={saveNota} disabled={savingNota || !nota.trim()} style={{ width: '100%', marginTop: 10, padding: 12, borderRadius: 12, background: nota.trim() ? T.accent : T.cardB, color: nota.trim() ? '#000' : T.muted, fontWeight: 700, border: 'none', cursor: nota.trim() ? 'pointer' : 'not-allowed', fontSize: 14 }}>
            {savingNota ? 'Guardando...' : 'Guardar nota'}
          </button>
        </Card>

        {/* Plan nutricional */}
        <SectionLabel>Plan nutricional / recomendaciones</SectionLabel>
        <Card>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 10, lineHeight: 1.4 }}>
            Pega aquí la dieta o las recomendaciones. El paciente podrá verlo y descargarlo como PDF desde su perfil.
          </div>
          <textarea
            value={plan}
            onChange={e => setPlan(e.target.value)}
            placeholder="Pega aquí el plan nutricional, la dieta o las recomendaciones..."
            style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: 12, color: T.text, fontSize: 13, fontFamily: 'DM Sans', minHeight: 160, resize: 'vertical', outline: 'none', lineHeight: 1.5 }}
          />
          <button onClick={savePlan} disabled={savingPlan} style={{ width: '100%', marginTop: 10, padding: 12, borderRadius: 12, background: T.accent, color: '#000', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: 14 }}>
            {savingPlan ? 'Guardando...' : 'Guardar plan'}
          </button>
        </Card>

      </div>

      {/* MODAL MEDICIÓN */}
      {showMetric && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowMetric(false) }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Nueva medición</div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Fecha</div>
              <input type="date" value={metricForm.fecha} max={todayStr()}
                onChange={e => setMetricForm(f => ({ ...f, fecha: e.target.value }))}
                style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }}
              />
            </div>

            {/* Tabs método */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[['bascula', '⚖️ Báscula'], ['pliegues', '📏 Pliegues']].map(([k, l]) => (
                <button key={k} onClick={() => setMetricTab(k)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${metricTab === k ? T.accent : T.cardB}`, background: metricTab === k ? T.accentSoft : T.bg, color: metricTab === k ? T.accent : T.muted, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{l}</button>
              ))}
            </div>

            {inp('Peso (kg)', 'peso_kg', 'ej: 72.4')}
            {inp('Perímetro abdominal (cm)', 'perimetro_abdominal_cm', 'ej: 86')}

            {metricTab === 'bascula' && <>
              {inp('% Grasa corporal (de la báscula)', 'grasa_pct', 'ej: 22')}
              {inp('% Masa magra (opcional)', 'masa_magra_pct', 'ej: 78')}
              {inp('Masa muscular (kg)', 'masa_muscular_kg', 'ej: 28.5')}
            </>}

            {metricTab === 'pliegues' && <>
              <div style={{ background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: T.accent }}>
                Se calculará el % grasa automáticamente con Jackson-Pollock
              </div>
              {inp('Pliegue tricipital (mm)', 'tricipital_mm', 'ej: 18')}
              {inp('Pliegue subescapular (mm)', 'subescapular_mm', 'ej: 14')}
              {inp('Pliegue suprailiaco (mm)', 'suprailiaco_mm', 'ej: 22')}
              {inp('Pliegue abdominal (mm)', 'abdominal_mm', 'ej: 25')}
              {inp('Pliegue muslo (mm)', 'muslo_mm', 'ej: 30')}
            </>}

            <button onClick={saveMetric} style={{ width: '100%', padding: 14, borderRadius: 14, background: T.accent, color: '#000', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', marginTop: 8 }}>
              Guardar medición
            </button>
          </div>
        </div>
      )}

      {/* MODAL OBJETIVOS */}
      {showGoals && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowGoals(false) }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Editar objetivos</div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>Tipo de objetivo</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['deficit', 'Déficit'], ['mantenimiento', 'Mantenimiento'], ['superavit', 'Superávit']].map(([k, l]) => (
                  <button key={k} onClick={() => setGoalForm(f => ({ ...f, tipo: k }))} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${goalForm.tipo === k ? T.accent : T.cardB}`, background: goalForm.tipo === k ? T.accentSoft : T.bg, color: goalForm.tipo === k ? T.accent : T.muted, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>{l}</button>
                ))}
              </div>
            </div>

            {goalInp('Calorías objetivo (kcal)', 'kcal_objetivo', 'ej: 1900')}
            {goalInp('Proteína (g)', 'proteina_objetivo', 'ej: 140')}
            {goalInp('Grasas (g)', 'grasa_objetivo', 'ej: 65')}
            {goalInp('Carbohidratos (g)', 'carbohidratos_objetivo', 'ej: 210')}
            {goalInp('Peso objetivo (kg)', 'peso_objetivo', 'ej: 62')}
            {goalInp('% Grasa objetivo', 'grasa_objetivo_pct', 'ej: 20')}

            <button onClick={saveGoals} style={{ width: '100%', padding: 14, borderRadius: 14, background: T.accent, color: '#000', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', marginTop: 8 }}>
              Guardar objetivos
            </button>
          </div>
        </div>
      )}

{/* MODAL REGISTRO DE COMIDAS */}
{showHistorial && (
  <div onClick={e => { if (e.target === e.currentTarget) setShowHistorial(false) }}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
    <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
      <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
        Registro del {historialFecha ? new Date(historialFecha).toLocaleDateString('es', { day: 'numeric', month: 'long' }) : ''}
      </div>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>{patient.nombre}</div>

      {!loadingHistorial && historialComidas.length > 0 && (() => {
        const totalKcal = historialComidas.reduce((s, meal) =>
          s + (meal.meal_items ?? []).reduce((s2, i) => s2 + (i.foods?.kcal_100g * i.cantidad_gramos / 100 || 0), 0), 0)
        const kcalObjActual = patient.goals?.kcal_objetivo ?? 2000
        const tdeeActual = patient.activityProfile?.tdee ?? null
        const sorted = [...targetsHistory].sort((a, b) => (a.vigente_desde < b.vigente_desde ? -1 : 1))
        const { kcalObj, tdee } = resolveTargetsForDate(sorted, historialFecha ?? todayStr(), kcalObjActual, tdeeActual)
        const isApprox = tdee == null || tdee <= 0
        const balance = Math.round(isApprox ? totalKcal - kcalObj : totalKcal - tdee)
        return (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <div style={{ flex: 1, background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: T.muted, marginBottom: 2 }}>Ingeridas</div>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'DM Mono', color: T.blue }}>{Math.round(totalKcal)} kcal</div>
            </div>
            <div style={{ flex: 1, background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: T.muted, marginBottom: 2 }}>Balance{isApprox ? ' (aprox.)' : ''}</div>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'DM Mono', color: balance <= 0 ? T.accent : T.danger }}>
                {balance > 0 ? '+' : ''}{balance} kcal
              </div>
            </div>
          </div>
        )
      })()}

      {loadingHistorial ? (
        <div style={{ textAlign: 'center', color: T.muted, padding: 20 }}>Cargando...</div>
      ) : historialComidas.length === 0 ? (
        <div style={{ textAlign: 'center', color: T.muted, padding: 20 }}>Sin registros para este día</div>
      ) : historialComidas.map((meal, mi) => {
        const items = meal.meal_items ?? []
        const mealKcal = items.reduce((s, i) => s + (i.foods?.kcal_100g * i.cantidad_gramos / 100 || 0), 0)
        const labels = { desayuno: '🍳 Desayuno', comida: '🍛 Comida', cena: '🌙 Cena', snack: '🍎 Snack' }
        return (
          <div key={mi} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.sub }}>{labels[meal.tipo] ?? meal.tipo}</span>
              <span style={{ fontSize: 12, color: T.muted, fontFamily: 'DM Mono' }}>{Math.round(mealKcal)} kcal</span>
            </div>
            {items.map((item, ii) => {
              const kcal = Math.round(item.foods?.kcal_100g * item.cantidad_gramos / 100 || 0)
              return (
                <div key={ii} style={{ background: T.bg, borderRadius: 10, padding: '8px 12px', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13 }}>{item.foods?.nombre} ({item.cantidad_gramos}g)</div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      P:{(item.foods?.proteina_100g * item.cantidad_gramos / 100).toFixed(1)}g
                      · G:{(item.foods?.grasa_100g * item.cantidad_gramos / 100).toFixed(1)}g
                      · C:{(item.foods?.carbohidratos_100g * item.cantidad_gramos / 100).toFixed(1)}g
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'DM Mono', color: T.accent }}>{kcal}</div>
                </div>
              )
            })}
          </div>
        )
      })}

      <button onClick={() => setShowHistorial(false)} style={{
        width: '100%', padding: 14, borderRadius: 14, background: T.cardB,
        color: T.text, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer'
      }}>
        Cerrar
      </button>
    </div>
  </div>
)}

{/* Eliminar paciente */}
<SectionLabel>
  Zona de peligro
</SectionLabel>
<div style={{ margin: '0 16px 32px' }}>
  {!confirmDelete ? (
    <button onClick={() => setConfirmDelete(true)} style={{
      width: '100%', padding: 12, borderRadius: 12,
      background: 'rgba(248,113,113,0.1)', border: `1px solid ${T.danger}`,
      color: T.danger, fontSize: 14, fontWeight: 600, cursor: 'pointer'
    }}>
      🗑️ Eliminar paciente
    </button>
  ) : (
    <div style={{ background: 'rgba(248,113,113,0.1)', border: `1px solid ${T.danger}`, borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.danger, marginBottom: 8 }}>
        ¿Eliminar a {patient.nombre}?
      </div>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 16, lineHeight: 1.5 }}>
        Se borrarán todos sus datos — comidas, métricas, notas y objetivos. Esta acción no se puede deshacer.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setConfirmDelete(false)} style={{
          flex: 1, padding: 12, borderRadius: 10,
          background: T.card, border: `1px solid ${T.cardB}`,
          color: T.text, fontSize: 14, cursor: 'pointer'
        }}>
          Cancelar
        </button>
        <button onClick={async () => {
          await eliminarPaciente(patient.id)
          onBack()
        }} style={{
          flex: 1, padding: 12, borderRadius: 10,
          background: T.danger, border: 'none',
          color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer'
        }}>
          Sí, eliminar
        </button>
      </div>
    </div>
  )}
</div>

      {showReport && (
        <PrintReport
          patient={patient}
          onClose={() => setShowReport(false)}
        />
      )}

      <ExportModal patients={[{ id: patient.id, nombre: patient.nombre }]} open={showExport} onClose={() => setShowExport(false)} />
      <ImportModal
        mode="professional"
        myPatients={[{ id: patient.id, nombre: patient.nombre }]}
        profesionalId={profesionalId}
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={refetch}
      />
    </>
  )
}